import React from 'react'
import TextField from '@mui/material/TextField'
import { SxProps, useTheme } from '@mui/material/styles'
import { Controller, useForm, UseFormReturn } from 'react-hook-form'
import {
  shouldDisplayError,
  deriveErrorMessage,
} from '../../utils/formErrorHandlers'

const defaultSx = (theme: any) => {
  return {
    textInputStyle: {
      width: '100%',
      fieldset: {
        borderRadius: theme.spacing(),
        border: '1px solid rgba(0, 0, 0, 0.2)',
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.07)',
      },
      '& .MuiInputBase-root': {
        padding: theme.spacing(0, 1.5),
      },
    },
    inputLabelStyle: {
      top: '-5px',
      color: '#000000B2',
      '&.Mui-focused': {
        visibility: 'hidden',
      },
    },
    mainInputStyle: {
      height: theme.spacing(5.25),
      padding: '0',
    },
    secondaryInputStyle: {
      height: theme.spacing(5.25),
    },
  }
}

// NOTE:
// To access the addition properties of the https://mui.com/material-ui/react-text-field/ component
// in future, add the require property to the TextFieldProps, e.g. maxRows

export interface TextFieldProp {
  autoFocus?: boolean
  disabled?: boolean
  error?: boolean
  form?: UseFormReturn
  helperText?: string
  id?: string
  inputProps?: object
  InputProps?: object
  InputLabelProps?: object
  label?: string
  name: string
  onBlur?: React.FocusEventHandler
  onKeyDown?: React.KeyboardEventHandler
  onKeyPress?: React.KeyboardEventHandler
  onChange?: React.ChangeEventHandler
  placeholder?: string
  required?: boolean
  rules?: object
  sx?: SxProps
  type?: React.HTMLInputTypeAttribute
  value?: string
  width?: number
}

const TextInput = (props: TextFieldProp) => {
  const theme = useTheme()
  const {
    mainInputStyle,
    secondaryInputStyle,
    inputLabelStyle,
    textInputStyle,
  } = defaultSx(theme)

  const handlerOnBlur = (event: React.FocusEvent, onBlurHook: Function) => {
    onBlurHook(event)
    props.onBlur && props.onBlur(event)
  }

  const handlerOnChange = (
    event: React.ChangeEvent,
    onChangeHook: Function,
  ) => {
    onChangeHook(event)
    props.onChange && props.onChange(event)
  }
  const rules = {
    required: props.required && `${props.label || 'Field'} is required`,
    validate: {
      ...props.rules,
    },
  }

  const useFormDefault = props.form || useForm()
  const {
    formState: { errors },
  } = useFormDefault

  // Controller: Wrapper component to make easier to work with Material-UI external controlled component.
  // React Hook Form embraces uncontrolled components and native inputs.
  // https://blog.logrocket.com/react-hook-form-complete-guide/

  return (
    <Controller
      control={useFormDefault.control}
      defaultValue={props.value}
      name={props.name}
      render={({
        field: { onChange: onChangeHook, onBlur: onBlurHook, value, name, ref },
      }) => (
        <TextField
          autoFocus={props.autoFocus}
          id={props.id || props.name}
          disabled={props.disabled}
          error={shouldDisplayError(errors, name, props.error)}
          helperText={deriveErrorMessage(errors, name, props.helperText)}
          inputProps={{
            sx: mainInputStyle,
            ...props.inputProps,
          }}
          InputProps={{
            sx: secondaryInputStyle,
            ...props.InputProps,
          }}
          InputLabelProps={{
            shrink: false,
            sx: inputLabelStyle,
            ...props.InputLabelProps,
          }}
          label={value ? '' : props.label}
          name={name || ''}
          fullWidth={true}
          onBlur={event => handlerOnBlur(event, onBlurHook)}
          onKeyDown={event => props.onKeyDown && props.onKeyDown(event)}
          onKeyPress={event => props.onKeyPress && props.onKeyPress(event)}
          onChange={event => handlerOnChange(event, onChangeHook)}
          placeholder={props.placeholder}
          sx={{ ...textInputStyle, ...props.sx }}
          type={props.type || 'text'}
          value={value || ''}
          FormHelperTextProps={{ sx: { display: 'none' } }}
        />
      )}
      rules={rules}
    />
  )
}

export default TextInput
